import { baxterMarkdownBlocks } from '@wordpuppi/shared';

/**
 * Baxter's ~40-line Markdown renderer (AB#645).
 *
 * ponytail: a regex pass over the subset a chat reply actually uses —
 * paragraphs, headings, **bold**, `code`, fenced code, bullets, numbered
 * lists, links. No dependency (react-markdown + remark is ~40 kB gzipped for
 * this). Upgrade path: if Baxter starts emitting tables/blockquotes/nested
 * lists, swap this one function for a real parser — nothing else changes.
 *
 * SECURITY: the source is escaped FIRST, so model output can never inject HTML;
 * link hrefs must match http(s) to render at all (no `javascript:`).
 */

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const inline = (s: string) =>
  escapeHtml(s)
    .replace(/`([^`]+)`/g, '<code class="rounded bg-muted px-1 py-0.5 text-[0.85em]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer noopener" class="text-primary underline underline-offset-2">$1</a>',
    );

/** Render a Markdown-ish assistant reply to sanitized HTML. */
export function renderMarkdown(src: string): string {
  return baxterMarkdownBlocks(src).map((block) => {
    if (block.kind === 'code') return `<pre class="my-2 overflow-x-auto rounded-lg border bg-muted/50 p-2 text-xs"><code>${escapeHtml(block.text)}</code></pre>`;
    if (block.kind === 'heading') return `<h${block.level} class="mt-3 mb-1 font-semibold">${inline(block.text)}</h${block.level}>`;
    if (block.kind === 'ul' || block.kind === 'ol') {
      const style = block.kind === 'ul' ? 'list-disc' : 'list-decimal';
      return `<${block.kind} class="my-2 ${style} pl-5 space-y-1">${block.items.map((item) => `<li>${inline(item)}</li>`).join('')}</${block.kind}>`;
    }
    return `<p class="my-1.5">${inline(block.text)}</p>`;
  }).join('');
}
